import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getCollections, getCustomerCollections, getCustomers, getCollectionsByDateRange, getFilteredCollections, updateCollection, updateAddedMilkRateCollection, deleteCollection } from '../services/api';
import { removeToken } from '../services/tokenStorage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faArrowLeft, faSearch, faTimes, faUserAlt, faCalendarAlt, faEdit, faEye, faCheck, faSpinner, faSortAmountDown, faSortAmountUp, faTrash } from '@fortawesome/free-solid-svg-icons';
import './PreviewCollections.css';
import { toast } from 'react-toastify';
import ProRataReportGenerator from './ProRataReportGenerator';

//0-39 snf min,0-19fat min,  130 > not allowed fat,110 > not alloed snf, 130< not allowed clr 340

// Additional styles for the date filter type toggle
const styles = {
  filterTypeToggle: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '15px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden'
  },
  toggleOption: {
    flex: '1',
    padding: '8px 12px',
    textAlign: 'center',
    cursor: 'pointer',
    position: 'relative'
  },
  toggleInput: {
    position: 'absolute',
    opacity: '0',
    width: '100%',
    height: '100%',
    top: '0',
    left: '0',
    margin: '0',
    cursor: 'pointer'
  },
  activeToggle: {
    backgroundColor: '#4a90e2',
    color: 'white'
  },
  inactiveToggle: {
    backgroundColor: '#f5f5f5',
    color: '#333'
  }
};

const PreviewCollections = () => {
  const [collections, setCollections] = useState([]);
  const [filteredCollections, setFilteredCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null); // Add success state for notifications
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50); // Changed from 10 to 50
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  const filteredCustomers = useMemo(() => {
    const trimmedSearch = customerSearchTerm.trim().toLowerCase();

    if (!trimmedSearch) {
      return customers;
    }

    return customers.filter(customer =>
      customer.name?.toLowerCase().includes(trimmedSearch) ||
      customer.customer_id?.toString().includes(trimmedSearch) ||
      customer.customer_code?.toString().includes(trimmedSearch)
    );
  }, [customers, customerSearchTerm]);

  // Date filter state
  const [showDateFilterModal, setShowDateFilterModal] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateFilterActive, setDateFilterActive] = useState(false);
  const [singleDateFilterMode, setSingleDateFilterMode] = useState(false);
  const [singleDate, setSingleDate] = useState('');
  
  // Add sort order state - default to descending (newest first)
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Edit collection state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [editFormErrors, setEditFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Preview state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewCollection, setPreviewCollection] = useState(null);
  
  // Add state for the edit customer modal
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [editCustomerSearchTerm, setEditCustomerSearchTerm] = useState('');
  const [filteredEditCustomers, setFilteredEditCustomers] = useState([]);
  const latestCustomerSearchRef = useRef({ view: 0, edit: 0 });

  const mergeCustomersIntoCache = useCallback((incoming = []) => {
    if (!incoming || incoming.length === 0) {
      return;
    }

    const normalizeCustomer = (customer) => ({
      ...customer,
      customer_id: customer?.customer_id ?? customer?.id ?? null
    });

    const normalizedIncoming = incoming
      .filter(Boolean)
      .map(normalizeCustomer)
      .filter(customer => customer.customer_id !== null || customer.id !== undefined);

    if (normalizedIncoming.length === 0) {
      return;
    }

    setCustomers(prev => {
      const map = new Map();
      (prev || []).forEach(customer => {
        if (!customer) return;
        const normalized = normalizeCustomer(customer);
        const key = normalized.id != null ? `id:${normalized.id}` : `cid:${normalized.customer_id}`;
        map.set(key, normalized);
      });
      normalizedIncoming.forEach(customer => {
        const key = customer.id != null ? `id:${customer.id}` : `cid:${customer.customer_id}`;
        map.set(key, customer);
      });
      return Array.from(map.values());
    });

    setFilteredEditCustomers(prev => {
      const map = new Map();
      normalizedIncoming.forEach(customer => {
        const key = customer.id != null ? `id:${customer.id}` : `cid:${customer.customer_id}`;
        map.set(key, customer);
      });
      (prev || []).forEach(customer => {
        if (!customer) return;
        const normalized = normalizeCustomer(customer);
        const key = normalized.id != null ? `id:${normalized.id}` : `cid:${normalized.customer_id}`;
        if (!map.has(key)) {
          map.set(key, normalized);
        }
      });
      return Array.from(map.values());
    });
  }, [setCustomers, setFilteredEditCustomers]);

  const resolveCustomerRecord = useCallback(async (source) => {
    if (!source) {
      return null;
    }

    const normalizeCustomer = (customer) => ({
      ...customer,
      customer_id: customer?.customer_id ?? customer?.id ?? null
    });

    const matchesRecord = (record) => {
      if (!record) return false;

      const recordId = record.id != null ? Number(record.id) : null;
      const recordCustomerId = record.customer_id != null ? String(record.customer_id) : null;

      const possibleSources = [];

      if (source.customer !== undefined && source.customer !== null) {
        if (typeof source.customer === 'object') {
          if (source.customer.id != null) {
            possibleSources.push({ type: 'id', value: Number(source.customer.id) });
          }
          if (source.customer.customer_id != null) {
            possibleSources.push({ type: 'customer_id', value: String(source.customer.customer_id) });
          }
        } else if (!Number.isNaN(Number(source.customer))) {
          possibleSources.push({ type: 'id', value: Number(source.customer) });
        }
      }

      if (source.customer_id !== undefined && source.customer_id !== null) {
        possibleSources.push({ type: 'customer_id', value: String(source.customer_id) });
      }

      if (source.customer_name) {
        possibleSources.push({ type: 'name', value: String(source.customer_name).toLowerCase() });
      }

      const uniqueSources = new Map();
      possibleSources.forEach(item => {
        const key = `${item.type}:${item.value}`;
        if (!uniqueSources.has(key)) {
          uniqueSources.set(key, item);
        }
      });

      for (const item of uniqueSources.values()) {
        if (item.type === 'id' && recordId != null && recordId === item.value) {
          return true;
        }
        if (item.type === 'customer_id' && recordCustomerId && recordCustomerId === item.value) {
          return true;
        }
        if (item.type === 'name' && record.name && record.name.toLowerCase() === item.value) {
          return true;
        }
      }

      return false;
    };

    const aggregatedSources = [
      ...(customers || []),
      ...(filteredCustomers || []),
      ...(filteredEditCustomers || [])
    ].map(normalizeCustomer);

    const directMatch = aggregatedSources.find(customer => matchesRecord(customer));
    if (directMatch && directMatch.id != null) {
      return {
        ...directMatch,
        id: Number(directMatch.id)
      };
    }

    const queries = [];

    const addQuery = (query) => {
      if (!query) return;
      const normalizedQuery = { ...query };
      if (normalizedQuery.id != null && !Number.isNaN(Number(normalizedQuery.id))) {
        normalizedQuery.id = Number(normalizedQuery.id);
      }
      if (normalizedQuery.customer_id != null) {
        normalizedQuery.customer_id = String(normalizedQuery.customer_id).trim();
      }
      if (normalizedQuery.search != null) {
        normalizedQuery.search = String(normalizedQuery.search).trim();
      }
      if (normalizedQuery.search === '') {
        delete normalizedQuery.search;
      }
      if (normalizedQuery.customer_id === '') {
        delete normalizedQuery.customer_id;
      }
      const key = JSON.stringify(normalizedQuery);
      if (!queries.some(existing => JSON.stringify(existing) === key)) {
        queries.push(normalizedQuery);
      }
    };

    if (source.customer !== undefined && source.customer !== null) {
      if (typeof source.customer === 'object') {
        if (source.customer.id != null) {
          addQuery({ id: source.customer.id, page_size: 1 });
        }
        if (source.customer.customer_id != null) {
          addQuery({ customer_id: source.customer.customer_id, page_size: 50 });
          addQuery({ search: source.customer.customer_id, page_size: 50 });
        }
        if (source.customer.name) {
          addQuery({ search: source.customer.name, page_size: 50 });
        }
      } else if (!Number.isNaN(Number(source.customer))) {
        addQuery({ id: Number(source.customer), page_size: 1 });
        addQuery({ customer_id: Number(source.customer), page_size: 50 });
      }
    }

    if (source.customer_id !== undefined && source.customer_id !== null && String(source.customer_id).trim() !== '') {
      const idString = String(source.customer_id).trim();
      addQuery({ customer_id: idString, page_size: 50 });
      addQuery({ search: idString, page_size: 50 });
    }

    if (source.customer_name) {
      addQuery({ search: source.customer_name, page_size: 50 });
    }

    for (const params of queries) {
      try {
        const response = await getCustomers(params);
        const rawResults = response?.results ?? (Array.isArray(response) ? response : []);
        const normalizedResults = (Array.isArray(rawResults) ? rawResults : [rawResults])
          .filter(Boolean)
          .map(normalizeCustomer);

        mergeCustomersIntoCache(normalizedResults);

        const match = normalizedResults.find(customer => matchesRecord(customer));
        if (match && match.id != null) {
          return {
            ...match,
            id: Number(match.id)
          };
        }
      } catch (error) {
        console.error('resolveCustomerRecord error with params', params, error);
      }
    }

    return null;
  }, [customers, filteredCustomers, filteredEditCustomers, mergeCustomersIntoCache]);

  const ensureCustomerIdForCollection = useCallback(async (collectionData) => {
    if (!collectionData) {
      return null;
    }

    const currentCustomerValue = collectionData.customer;
    if (currentCustomerValue !== undefined && currentCustomerValue !== null && !Number.isNaN(Number(currentCustomerValue))) {
      return Number(currentCustomerValue);
    }

    const resolvedRecord = await resolveCustomerRecord(collectionData);
    if (resolvedRecord && resolvedRecord.id != null) {
      const resolvedId = Number(resolvedRecord.id);
      setEditingCollection(prev => {
        if (!prev) {
          return prev;
        }

        const shouldUpdate = prev.id === collectionData.id || prev.customer === collectionData.customer;
        if (!shouldUpdate) {
          return prev;
        }

        return {
          ...prev,
          customer: resolvedId,
          customer_id: resolvedRecord.customer_id ?? prev.customer_id,
          customer_name: resolvedRecord.name ?? prev.customer_name
        };
      });

      mergeCustomersIntoCache([resolvedRecord]);

      return resolvedId;
    }

    return null;
  }, [resolveCustomerRecord, mergeCustomersIntoCache, setEditingCollection]);
  
  // Add state for milk rate changes
  const [showAddMilkRateModal, setShowAddMilkRateModal] = useState(false);
  const [showMilkRatePreviewModal, setShowMilkRatePreviewModal] = useState(false);
  const [milkRateFromDate, setMilkRateFromDate] = useState('');
  const [milkRateToDate, setMilkRateToDate] = useState('');
  const [currentMilkRate, setCurrentMilkRate] = useState({ milk_rate: 50 });
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [isLoadingRatePreview, setIsLoadingRatePreview] = useState(false);
  const [isSubmittingRates, setIsSubmittingRates] = useState(false);
  const [ratePreviewError, setRatePreviewError] = useState(null);

  // Add new state variables for milk rate single date filtering
  const [milkRateSingleDateFilterMode, setMilkRateSingleDateFilterMode] = useState(false);
  const [milkRateSingleDate, setMilkRateSingleDate] = useState('');

  // Add state variables for delete functionality
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const navigate = useNavigate();

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  }; 

  // Update fetchCollectionsBasedOnFilters for date filter
  const fetchCollectionsBasedOnFilters = useCallback(async (initialLoad = false) => {
    setLoading(true);
    setError(null);
    
    try {
      if (initialLoad) {
        setCollections([]);
        setFilteredCollections([]);
      }
      
      let response;
      
      // Determine which API call to use based on active filters
      if (selectedCustomer && dateFilterActive) {
        // Both customer and date filters are active - use getFilteredCollections
        const options = { 
          customerId: selectedCustomer.id,
          ordering: sortOrder === 'asc' ? 'created_at' : '-created_at' // Add ordering parameter based on sortOrder
        };
        
        if (singleDateFilterMode) {
          options.dateFrom = singleDate;
          options.dateTo = singleDate;
          console.log(`Fetching collections for customer ${selectedCustomer.name} on ${singleDate}`);
        } else {
          options.dateFrom = fromDate;
          options.dateTo = toDate;
          console.log(`Fetching collections for customer ${selectedCustomer.name} from ${fromDate} to ${toDate}`);
        }
        
        response = await getFilteredCollections(options);
        console.log('Fetched filtered collections:', response);
        
        let collectionsData = [];
        if (Array.isArray(response)) {
          collectionsData = response;
        } else if (response && response.results) {
          collectionsData = response.results;
        }
        
        setCollections(collectionsData);
        setFilteredCollections(collectionsData); // No need to sort again as API handles ordering
        setHasMore(false);
      } else if (dateFilterActive) {
        // Only date filter active
        let ordering = sortOrder === 'asc' ? 'created_at' : '-created_at';
        
        if (singleDateFilterMode) {
          console.log(`DIRECT API CALL: Fetching ALL collections on ${singleDate} ordered by ${ordering}`);
          // Using only date parameters with extremely large limit to get ALL records
          response = await getCollectionsByDateRange(singleDate, singleDate, ordering);
        } else {
          console.log(`DIRECT API CALL: Fetching ALL collections from ${fromDate} to ${toDate} ordered by ${ordering}`);
          // Using only date parameters with extremely large limit to get ALL records
          response = await getCollectionsByDateRange(fromDate, toDate, ordering);
        }
        
        // Log the response format to debug
        console.log(`Response type: ${typeof response}`);
        if (Array.isArray(response)) {
          console.log(`Received array with ${response.length} collections`);
        } else if (response && typeof response === 'object') {
          if (response.results) {
            console.log(`Received object with ${response.results.length} collections`);
          } else {
            console.log('Received object without results property:', Object.keys(response));
          }
        } else {
          console.log('Unexpected response format:', response);
        }
        
        // Safety check for response format
        let collectionsData = [];
        if (Array.isArray(response)) {
          collectionsData = response;
        } else if (response && response.results) {
          collectionsData = response.results;
        }
        
        // Set the data
        setCollections(collectionsData);
        setFilteredCollections(collectionsData); // No need to sort again as API handles ordering
        setHasMore(false);
      } else if (selectedCustomer) {
        // Only customer filter active
        console.log(`Fetching all collections for customer ID ${selectedCustomer.id} ordered by ${sortOrder === 'asc' ? 'created_at' : '-created_at'}`);
        response = await getCustomerCollections(selectedCustomer.id, {
          ordering: sortOrder === 'asc' ? 'created_at' : '-created_at'
        });
        console.log('Fetched collections for customer:', selectedCustomer.name);
        
        // Similarly process the customer filter response
        let collectionsData = [];
        if (Array.isArray(response)) {
          collectionsData = response;
        } else if (response && response.results) {
          collectionsData = response.results;
        }
        
        setCollections(collectionsData);
        setFilteredCollections(collectionsData); // No need to sort again as API handles ordering
        setHasMore(false);
      } else {
        // No filters active - use normal pagination
        const params = {
          page: page,
          limit: pageSize,
          ordering: sortOrder === 'asc' ? 'created_at' : '-created_at' // Add ordering parameter
        };
        
        response = await getCollections(params);
        
        const newCollections = response.results || [];
        
        // If first page or initialLoad, replace the collections
        // Otherwise append the new collections
        setCollections((prevCollections) => {
          if (page === 1 || initialLoad) {
            return newCollections;
          }
          return [...prevCollections, ...newCollections];
        });
        
        // Apply the filtered collections
        if (page === 1 || initialLoad) {
          setFilteredCollections(newCollections);
        } else {
          setFilteredCollections(prev => [...prev, ...newCollections]);
        }
        
        // Check if there's more data to load
        setHasMore(response.next !== null);
      }
    } catch (err) {
      console.error('Error fetching collections:', err);
      let errorMessage = 'Failed to load collections. Please try again later.';
      
      if (selectedCustomer && dateFilterActive) {
        if (singleDateFilterMode) {
          errorMessage = `Failed to load collections for ${selectedCustomer.name} on ${formatDateForDisplay(singleDate)}.`;
        } else {
          errorMessage = `Failed to load collections for ${selectedCustomer.name} between ${formatDateForDisplay(fromDate)} and ${formatDateForDisplay(toDate)}.`;
        }
      } else if (selectedCustomer) {
        errorMessage = `Failed to load collections for ${selectedCustomer.name}.`;
      } else if (dateFilterActive) {
        if (singleDateFilterMode) {
          errorMessage = `Failed to load collections on ${formatDateForDisplay(singleDate)}.`;
        } else {
          errorMessage = `Failed to load collections between ${formatDateForDisplay(fromDate)} and ${formatDateForDisplay(toDate)}.`;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedCustomer, dateFilterActive, fromDate, toDate, singleDate, singleDateFilterMode, page, pageSize, sortOrder]);

  // Effect to fetch collections based on filters and pagination
  useEffect(() => {
    console.log('Fetching collections based on filters and pagination', {
      dateFilterActive,
      selectedCustomer,
      page,
      singleDateFilterMode,
      singleDate,
      fromDate,
      toDate
    });
    
    fetchCollectionsBasedOnFilters();
  }, [fetchCollectionsBasedOnFilters, dateFilterActive, fromDate, page, selectedCustomer, singleDate, singleDateFilterMode, toDate]);

  // Reset to page 1 when filters change
  useEffect(() => {
    console.log('Filters changed, resetting to page 1');
    setPage(1);
  }, [selectedCustomer, dateFilterActive, fromDate, toDate, singleDate, singleDateFilterMode]);

  // Use a separate useEffect to handle search filtering
  useEffect(() => {
    console.log('Search term or collections changed, applying search filter');
    
    if (!collections || collections.length === 0) {
      return;
    }
    
    let filtered = collections;
    
    // Apply search filter if needed
    if (searchTerm.trim() !== '') {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = collections.filter(collection => 
        (collection.customer_name && collection.customer_name.toLowerCase().includes(lowercasedSearch)) ||
        (collection.customer_id && String(collection.customer_id).includes(lowercasedSearch)) ||
        (collection.customer_code && String(collection.customer_code).includes(lowercasedSearch))
      );
      console.log(`Found ${filtered.length} collections matching search "${searchTerm}"`);
    }
    
    // Sort the filtered results using created_at field as per API documentation
    const sortedFiltered = [...filtered].sort((a, b) => {
      // Primary sort by created_at timestamp
      const createdAtA = new Date(a.created_at);
      const createdAtB = new Date(b.created_at);
      
      return sortOrder === 'asc' ? createdAtA - createdAtB : createdAtB - createdAtA;
    });
    
    setFilteredCollections(sortedFiltered);
  }, [searchTerm, collections, sortOrder]);

  // Update the load more button to be hidden when filters are active
  const showLoadMoreButton = !loading && hasMore && !dateFilterActive && !selectedCustomer;

  // When filters change, make sure to reset collections if needed
  useEffect(() => {
    if (dateFilterActive || selectedCustomer) {
      // Reset to page 1 and set hasMore to false when filters are active
      setPage(1);
      setHasMore(false);
      
      // If the user had a filter active and then changed it, we need to re-fetch
      // This will trigger fetchCollectionsBasedOnFilters through the dependency
      console.log('Filter changed, new fetch will be triggered with reset state');
    }
  }, [dateFilterActive, selectedCustomer, fromDate, toDate, singleDate, singleDateFilterMode]);

  // Fetch all customers when modal opens
  const openCustomerModal = async () => {
    setShowCustomerModal(true);
    setCustomerSearchTerm('');

    const requestId = Date.now();
    latestCustomerSearchRef.current.view = requestId;

    try {
      setLoadingCustomers(true);
      const response = await getCustomers({ page_size: 50 });
      if (latestCustomerSearchRef.current.view !== requestId) {
        return;
      }

      const customerData = response?.results ?? (Array.isArray(response) ? response : []);
      const normalizedCustomers = customerData.map(customer => ({
        ...customer,
        customer_id: customer.customer_id ?? customer.id
      }));

      setCustomers(normalizedCustomers);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers. Please try again later.');
    } finally {
      if (latestCustomerSearchRef.current.view === requestId) {
        setLoadingCustomers(false);
      }
    }
  };

  const openDateFilterModal = () => {
    const today = new Date();
    
    // Initialize with current date if not set
    if (!fromDate) {
      const defaultFromDate = new Date();
      defaultFromDate.setDate(defaultFromDate.getDate() - 7); // Default to 7 days ago
      setFromDate(formatDateForInput(defaultFromDate));
    }
    
    if (!toDate) {
      setToDate(formatDateForInput(today));
    }
    
    if (!singleDate) {
      setSingleDate(formatDateForInput(today));
    }
    
    setShowDateFilterModal(true);
  };

  const formatDateForInput = (date) => {
    const d = new Date(date);
    const month = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  };

  const formatDateForDisplay = (dateString) => {
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  // Apply date filter directly without any pagination
  const applyDateFilter = () => {
    if (singleDateFilterMode) {
      if (!singleDate) {
        alert('Please select a date');
        return;
      }
      
      // First close the modal
      setShowDateFilterModal(false);
      
      console.log('APPLYING SINGLE DATE FILTER:', singleDate);
      
      // Show loading
      setLoading(true);
      
      // Clear collections first
      setCollections([]);
      setFilteredCollections([]);
      
      // Then set filter as active
      setDateFilterActive(true);
      setPage(1);
      setHasMore(false);
    } else {
      if (!fromDate || !toDate) {
        alert('Please select both from and to dates');
        return;
      }
      
      // Validate date range
      if (new Date(toDate) < new Date(fromDate)) {
        alert('To date cannot be earlier than from date');
        return;
      }
      
      // First close the modal
      setShowDateFilterModal(false);
      
      console.log('APPLYING DATE RANGE FILTER:', fromDate, 'to', toDate);
      
      // Show loading
      setLoading(true);
      
      // Clear collections first
      setCollections([]);
      setFilteredCollections([]);
      
      // Then set filter as active
      setDateFilterActive(true);
      setPage(1);
      setHasMore(false);
    }
  };

  const clearDateFilter = () => {
    // Reset all date filter state
    setDateFilterActive(false);
    setFromDate('');
    setToDate('');
    setSingleDate('');
    
    // Reset to page 1 to fetch first page of unfiltered data
    setPage(1);
    
    // This will trigger the fetchCollectionsBasedOnFilters through useEffect dependencies
    console.log('Date filter cleared, fetching unfiltered data');
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setPage(1); // Explicitly reset to page 1
    setShowCustomerModal(false);
  };

  const clearCustomerFilter = () => {
    setSelectedCustomer(null);
    setPage(1); // Explicitly reset to page 1
  };

  // Update loadMoreCollections to always load 50 more by default
  const loadMoreCollections = () => {
    if (!loading && hasMore) {
      console.log(`Loading more collections, incrementing page from ${page} to ${page + 1}`);
      
      // Always load 50 records at a time
      setPageSize(50);
      
      setPage(prevPage => prevPage + 1);
    } else {
      console.log('Cannot load more:', { loading, hasMore });
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleCustomerSearchChange = useCallback(async (e) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);

    const trimmedValue = value.trim();
    const requestId = Date.now();
    latestCustomerSearchRef.current.view = requestId;

    if (!trimmedValue) {
      try {
        const response = await getCustomers({ page_size: 50 });
        if (latestCustomerSearchRef.current.view !== requestId) {
          return;
        }

        const customerData = response?.results ?? (Array.isArray(response) ? response : []);
        const normalizedCustomers = customerData.map(customer => ({
          ...customer,
          customer_id: customer.customer_id ?? customer.id
        }));

        setCustomers(normalizedCustomers);
      } catch (err) {
        console.error('Error fetching default customers:', err);
      }
      return;
    }

    const params = {
      search: trimmedValue,
      page_size: 50
    };

    if (/^\d+$/.test(trimmedValue)) {
      params.customer_id = trimmedValue;
    }

    try {
      const response = await getCustomers(params);
      if (latestCustomerSearchRef.current.view !== requestId) {
        return;
      }

      const customerData = response?.results ?? (Array.isArray(response) ? response : []);
      const normalizedCustomers = customerData.map(customer => ({
        ...customer,
        customer_id: customer.customer_id ?? customer.id
      }));

      setCustomers(prev => {
        const existingIds = new Set(prev.map(item => item.id ?? item.customer_id));
        const merged = [...prev];

        normalizedCustomers.forEach(customer => {
          const key = customer.id ?? customer.customer_id;
          if (!existingIds.has(key)) {
            merged.push(customer);
            existingIds.add(key);
          }
        });

        return merged;
      });
    } catch (err) {
      console.error('Error searching customers:', err);
      if (latestCustomerSearchRef.current.view !== requestId) {
        return;
      }

      const loweredQuery = trimmedValue.toLowerCase();
      const fallbackResults = customers.filter(customer => {
        const idString = customer.customer_id?.toString() || '';
        const normalizedName = customer.name?.toLowerCase() || '';
        return idString.includes(loweredQuery) || normalizedName.includes(loweredQuery);
      });

      setCustomers(fallbackResults);
    }
  }, [customers]);

  const formatDate = (dateString, time) => {
    if (!dateString) return 'N/A';
    
    // Parse the dateString into a Date object
    const dateParts = dateString.split('-');
    if (dateParts.length !== 3) return dateString; // Return as-is if format unexpected
    
    const dateObj = new Date(dateString);
    
    // Format date as DD MMM YY
    const formattedDate = dateObj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit'
    });
    
    // Add AM/PM based on collection time
    // Handle both formats: time values ('06:00') and text values ('morning')
    let timeIndicator;
    if (time === 'morning' || (time && time.startsWith('06'))) {
      timeIndicator = 'AM';
    } else {
      timeIndicator = 'PM';
    }
    
    return `${formattedDate} (${timeIndicator})`;
  };

  // Format number to 2 decimal places
  const formatNumber = (number) => {
    if (number === undefined || number === null) return 'N/A';
    return Number(number).toFixed(2);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return 'N/A';
    return `₹${Number(amount).toFixed(2)}`;
  };

  // Get active filter description for display purposes
  // This function is used in the UI to display current active filters
  // eslint-disable-next-line no-unused-vars
  const getActiveFilterDescription = () => {
    let description = '';
    
    if (selectedCustomer && dateFilterActive) {
      if (singleDateFilterMode) {
        description = `${selectedCustomer.name}'s collections on ${formatDateForDisplay(singleDate)}`;
      } else {
        description = `${selectedCustomer.name}'s collections from ${formatDateForDisplay(fromDate)} to ${formatDateForDisplay(toDate)}`;
      }
    } else if (selectedCustomer) {
      description = `${selectedCustomer.name}'s collections`;
    } else if (dateFilterActive) {
      if (singleDateFilterMode) {
        description = `Collections on ${formatDateForDisplay(singleDate)}`;
      } else {
        description = `Collections from ${formatDateForDisplay(fromDate)} to ${formatDateForDisplay(toDate)}`;
      }
    } else if (searchTerm) {
      description = `Collections matching "${searchTerm}"`;
    } else {
      description = 'All collections';
    }
    
    // Update the sort order description to be explicit about using created_at
    description += ` (sorted by ${sortOrder === 'desc' ? 'newest' : 'oldest'} creation time)`;
    
    return description;
  };

  // Calculate SNF from CLR - same as in BulkCollectionV1
  const calculateSnfFromClr = (clrValue, fatValue) => {
    // Ensure we have valid inputs
    if (!clrValue || !fatValue) {
      return null; // Return null instead of 0
    }
    
    // Convert strings to numbers to ensure proper calculation
    const clr = parseFloat(clrValue);
    const fat = parseFloat(fatValue);
    
    // Additional validation for reasonable values
    if (isNaN(clr) || isNaN(fat) || clr <= 0 || fat <= 0) {
      return null; // Return null instead of 0
    }
    
    // SNF calculation formula: SNF = (CLR / 4) + (0.20 * FAT) + 0.14
    const calculatedSnf = Math.floor(((clr / 4) + (fat * 0.20) + 0.14) * 100) / 100;
    
    return calculatedSnf;
  };

  // Calculate derived values - same as in BulkCollectionV1
  const calculateDerivedValues = (collection) => {
    const fat = parseFloat(collection.fat_percentage) || 0;
    const snf = parseFloat(collection.snf_percentage) || 0;
    const weight = parseFloat(collection.kg) || 0;
    const rate = parseFloat(collection.milk_rate) || 0;
    const baseSnfPercentage = parseFloat(collection.base_snf_percentage) || 9.0;
    
    // Calculate fat_kg and snf_kg
    const fatKg = Math.floor((weight * (fat / 100)) * 100) / 100;
    const snfKg = Math.floor((weight * (snf / 100)) * 100) / 100;

    // Calculate rates based on fat and SNF components
    const fatRate = Math.floor((rate * 60 / 6.5) * 100) / 100;
    const snfRate = Math.floor((rate * 40 / baseSnfPercentage) * 100) / 100;
    
    // Calculate amount based on fat, snf rates and components with proper rounding
    const fatAmount = Math.floor(parseFloat(fatKg) * parseFloat(fatRate) * 100) / 100;
    const snfAmount = Math.floor(parseFloat(snfKg) * parseFloat(snfRate) * 100) / 100;
    const amount = parseFloat((fatAmount + snfAmount).toFixed(2));

    // Calculate solid weight
    const solidWeight = (amount / rate).toFixed(3);
    
    return {
      fat_kg: fatKg,
      snf_kg: snfKg,
      fat_rate: fatRate,
      snf_rate: snfRate,
      solid_weight: solidWeight,
      amount: Math.round(amount * 100) / 100
    };
  };

  // Format collection for API submission with consistent calculations
  const formatCollectionForAPI = (editingCollection, customerId) => {
    // Make sure customer is sent as a number
    const customerIdNum = typeof customerId === 'string' && !isNaN(parseInt(customerId)) 
      ? parseInt(customerId) 
      : customerId;
    
    // Handle CLR - use 0 instead of null, like in BulkCollectionV1
    const finalClrValue = parseFloat(editingCollection.clr) || 0;
    
    // Get base values
    const weight = parseFloat(editingCollection.kg) || 0;
    const fat = parseFloat(editingCollection.fat_percentage) || 0;
    const snf = editingCollection.snf_percentage;
    const milkRate = parseFloat(editingCollection.milk_rate) || 0;
    
    // Ensure base_fat_percentage is valid (use fat percentage as base)
    const baseFatPercentage = 6.5;
    
    // Calculate liters from kg using the same formula as BulkCollectionV1
    const liters = Math.floor((weight / 1.02249) * 100) / 100;
    
    // Calculate fat_kg and snf_kg to ensure consistency
    const fatKg = Math.floor((weight * (fat / 100)) * 100) / 100;
    const snfKg = Math.floor((weight * (snf / 100)) * 100) / 100;
      
    // Handle milk_type consistently with BulkCollectionV1
    const milkType = editingCollection.milk_type === 'cow+buffalo' ? 'cow_buffalo' : editingCollection.milk_type || 'cow';

    // Get base SNF percentage value - use base_snf_percentage which is the correct API field name
    let baseSnfPercentage = 9.0;
    if (editingCollection.base_snf_percentage && editingCollection.base_snf_percentage !== '') {
      baseSnfPercentage = parseFloat(editingCollection.base_snf_percentage);
    } else if (editingCollection.base_snf && editingCollection.base_snf !== '') {
      // Fall back to base_snf if base_snf_percentage is not available, but this shouldn't happen
      baseSnfPercentage = parseFloat(editingCollection.base_snf);
      console.warn('Using base_snf instead of base_snf_percentage. This should be fixed in the code.');
    }
    console.log('Using base_snf_percentage for API:', baseSnfPercentage);
    
    const fatRate = Math.floor((milkRate * 60 / 6.5) * 100) / 100;
    const snfRate = Math.floor((milkRate * 40 / baseSnfPercentage) * 100) / 100;
    
    // Calculate amount consistently and properly round to 2 decimal places
    // Using toFixed(2) and parseFloat to prevent excessively long decimal representations
    const fatAmount = Math.floor(parseFloat(fatKg) * parseFloat(fatRate) * 100) / 100;
    const snfAmount = Math.floor(parseFloat(snfKg) * parseFloat(snfRate) * 100) / 100;
    const amount = parseFloat((fatAmount + snfAmount).toFixed(2));

    // Calculate solid weight consistently
    const solidWeight = parseFloat((amount / milkRate).toFixed(3));
    
    // Prepare collection data for API using only valid fields from API docs
    const formattedCollection = {
      customer: customerIdNum,
      customer_name: editingCollection.customer_name || '', // Add customer_name for display in the preview
      collection_date: editingCollection.collection_date,
      collection_time: editingCollection.collection_time, // "morning" or "evening"
      milk_type: milkType,
      measured: 'kg',
      kg: weight,
      liters: liters,
      fat_percentage: fat,
      snf_percentage: snf,
      base_snf_percentage: baseSnfPercentage, // Only use base_snf_percentage, which is the correct API field
      clr: finalClrValue,
      fat_kg: fatKg,
      snf_kg: snfKg,
      fat_rate: fatRate,
      snf_rate: snfRate,
      milk_rate: milkRate,
      amount: amount, // Round to 2 decimal places
      solid_weight: solidWeight,
      base_fat_percentage: baseFatPercentage
    };
    
    // eslint-disable-next-line no-unused-vars
    const customerName = editingCollection.customer_name;
    
    // Ensure all numeric values are actual numbers, not strings
    Object.keys(formattedCollection).forEach(key => {
      if (typeof formattedCollection[key] === 'number' && isNaN(formattedCollection[key])) {
        if (['fat_percentage', 'base_fat_percentage'].includes(key)) {
          formattedCollection[key] = 6.5;
        } else if (['snf_percentage', 'base_snf_percentage'].includes(key)) {
          formattedCollection[key] = 9.0;
        } else if (['fat_rate', 'snf_rate', 'fat_kg', 'snf_kg', 'solid_weight'].includes(key)) {
          formattedCollection[key] = 0;
        } else if (key === 'milk_rate') {
          formattedCollection[key] = 50.0;
        } else if (key === 'amount') {
          formattedCollection[key] = 0;
        }
      }
    });
    
    return formattedCollection;
  };

  // Handle edit button click
  const handleEditClick = async (collection) => {
    console.log('Collection data from API:', JSON.stringify(collection, null, 2));

    let resolvedCustomerRecord = await resolveCustomerRecord(collection);
    let resolvedCustomerId = resolvedCustomerRecord?.id != null ? Number(resolvedCustomerRecord.id) : null;

    if (!resolvedCustomerId && collection.customer_db_id != null) {
      resolvedCustomerId = Number(collection.customer_db_id);
    }

    if (!resolvedCustomerId && collection.customer != null) {
      if (typeof collection.customer === 'object' && collection.customer.id != null) {
        resolvedCustomerId = Number(collection.customer.id);
        resolvedCustomerRecord = resolvedCustomerRecord || {
          ...collection.customer,
          id: resolvedCustomerId,
          customer_id: collection.customer.customer_id ?? collection.customer_id ?? resolvedCustomerId,
          name: collection.customer.name ?? collection.customer_name ?? ''
        };
      } else if (!Number.isNaN(Number(collection.customer))) {
        resolvedCustomerId = Number(collection.customer);
      }
    }

    if (!resolvedCustomerId && collection.customer_id != null && !Number.isNaN(Number(collection.customer_id))) {
      resolvedCustomerId = Number(collection.customer_id);
    }

    if (!resolvedCustomerRecord && resolvedCustomerId != null) {
      resolvedCustomerRecord = {
        id: resolvedCustomerId,
        customer_id: collection.customer_id ?? resolvedCustomerId,
        name: collection.customer_name ?? (typeof collection.customer === 'object' ? collection.customer?.name : '') ?? ''
      };
    }

    if (resolvedCustomerRecord) {
      mergeCustomersIntoCache([resolvedCustomerRecord]);
    }

    if (!resolvedCustomerId) {
      console.error('Unable to resolve customer ID for collection', collection);
      setError('Unable to find customer information. Please re-select the customer before editing.');
      return;
    }

    let collectionTimeValue = 'morning';
    if (collection.collection_time) {
      if (collection.collection_time === 'morning' || collection.collection_time === 'evening') {
        collectionTimeValue = collection.collection_time;
      } else if (typeof collection.collection_time === 'string') {
        const hourNumber = parseInt(collection.collection_time.split(':')[0], 10);
        collectionTimeValue = hourNumber < 12 ? 'morning' : 'evening';
      }
    }

    const parseNumericValue = (value) => {
      if (value === undefined || value === null) return '';
      const parsed = parseFloat(value);
      return isNaN(parsed) ? '' : parsed.toString();
    };

    let baseSnfPercentage = '9.0';
    if (collection.base_snf_percentage !== undefined && collection.base_snf_percentage !== null) {
      baseSnfPercentage = parseNumericValue(collection.base_snf_percentage);
    } else if (collection.base_snf !== undefined && collection.base_snf !== null) {
      baseSnfPercentage = parseNumericValue(collection.base_snf);
      console.warn('Using base_snf as fallback for base_snf_percentage. This should be fixed in the data.');
    }
    console.log('Loading base_snf_percentage into form:', baseSnfPercentage);

    const updatedEditingCollection = {
      ...collection,
      customer_name: resolvedCustomerRecord.name || '',
      customer_id: resolvedCustomerRecord.customer_id,
      customer: resolvedCustomerId,
      collection_date: collection.collection_date,
      collection_time: collectionTimeValue,
      fat_percentage: parseNumericValue(collection.fat_percentage),
      snf_percentage: parseNumericValue(collection.snf_percentage),
      clr: collection.clr ? parseNumericValue(collection.clr) : '',
      kg: parseNumericValue(collection.kg),
      base_snf_percentage: baseSnfPercentage,
      milk_rate: parseNumericValue(collection.milk_rate)
    };

    setEditingCollection(updatedEditingCollection);

    console.log('Editing collection with customer database ID:', resolvedCustomerId);

    setEditFormErrors({});
    setShowEditModal(true);

    await ensureCustomerIdForCollection(updatedEditingCollection);
  };

  // Handle form input changes
  const handleEditInputChange = (field, value) => {
    // Validate numeric inputs
    if (['fat_percentage', 'snf_percentage', 'clr', 'kg', 'base_snf_percentage', 'milk_rate'].includes(field)) {
      // Allow empty value for clearing the field
      if (value === '') {
        // Allow empty input
      }
      // For CLR and other fields, we only allow numbers with up to 2 decimal places
      else if (field === 'clr') {
        const regex = /^\d*\.?\d{0,2}$/;
        if (!regex.test(value)) return;
      }
      // For all other numeric fields
      else {
        const regex = /^\d*\.?\d{0,2}$/;
        if (!regex.test(value)) return;
      }
    }

    const updatedCollection = { ...editingCollection, [field]: value };
    
    // Handle case where the form field is still called base_snf but we need to update base_snf_percentage
    if (field === 'base_snf') {
      updatedCollection.base_snf_percentage = value;
      console.log('Updated base_snf_percentage to:', value);
    }
    
    // If CLR is changed and fat is present, calculate SNF
    if (field === 'clr' && value && updatedCollection.fat_percentage) {
      const calculatedSnf = calculateSnfFromClr(value, updatedCollection.fat_percentage);
      // Only update SNF if calculation returned a valid value
      if (calculatedSnf !== null) {
        updatedCollection.snf_percentage = calculatedSnf.toString();
      }
      
      // Recalculate derived values if all required fields are present
      if (updatedCollection.kg && updatedCollection.snf_percentage) {
        const derived = calculateDerivedValues(updatedCollection);
        updatedCollection.fat_kg = derived.fat_kg;
        updatedCollection.snf_kg = derived.snf_kg;
        updatedCollection.amount = derived.amount;
        updatedCollection.fat_rate = derived.fat_rate;
        updatedCollection.snf_rate = derived.snf_rate;
      }
    }
    
    // If fat is changed and CLR is present, recalculate SNF
    // Only recalculate if fat has an actual value (not empty)
    if (field === 'fat_percentage' && value !== '' && updatedCollection.clr) {
      const calculatedSnf = calculateSnfFromClr(updatedCollection.clr, value);
      // Only update SNF if calculation returned a valid value
      if (calculatedSnf !== null) {
        updatedCollection.snf_percentage = calculatedSnf.toString();
      }
      
      // Recalculate derived values if weight is present
      if (updatedCollection.kg && updatedCollection.snf_percentage) {
        const derived = calculateDerivedValues(updatedCollection);
        updatedCollection.fat_kg = derived.fat_kg;
        updatedCollection.snf_kg = derived.snf_kg;
        updatedCollection.amount = derived.amount;
        updatedCollection.fat_rate = derived.fat_rate;
        updatedCollection.snf_rate = derived.snf_rate;
      }
    }
    
    // Update dependent calculations if fat, snf, weight, base_snf_percentage, or milk_rate changes
    // Only do this if we have all the required values (non-empty)
    if (['fat_percentage', 'snf_percentage', 'kg', 'base_snf', 'base_snf_percentage', 'milk_rate'].includes(field) && 
        updatedCollection.fat_percentage && updatedCollection.fat_percentage !== '' && 
        updatedCollection.snf_percentage && updatedCollection.snf_percentage !== '' && 
        updatedCollection.kg && updatedCollection.kg !== '') {
      
      const derived = calculateDerivedValues(updatedCollection);
      updatedCollection.fat_kg = derived.fat_kg;
      updatedCollection.snf_kg = derived.snf_kg;
      updatedCollection.amount = derived.amount;
      updatedCollection.fat_rate = derived.fat_rate;
      updatedCollection.snf_rate = derived.snf_rate;
    }
    
    setEditingCollection(updatedCollection);
    
    // Clear any errors for this field
    if (editFormErrors[field]) {
      const updatedErrors = { ...editFormErrors };
      delete updatedErrors[field];
      setEditFormErrors(updatedErrors);
    }
  };

  // Validate the edited collection
  const validateEditForm = () => {
    const errors = {};
    
    if (!editingCollection.customer_name) {
      errors.customer_name = 'Customer name is required';
    }
    
    if (!editingCollection.collection_date) {
      errors.collection_date = 'Collection date is required';
    }
    
    if (!editingCollection.fat_percentage) {
      errors.fat_percentage = 'Fat percent is required';
    } else if (isNaN(editingCollection.fat_percentage) || parseFloat(editingCollection.fat_percentage) <= 0) {
      errors.fat_percentage = 'Valid fat percent is required';
    } else if (parseFloat(editingCollection.fat_percentage) > 15) {
      errors.fat_percentage = 'Fat percent cannot exceed 15%';
    }
    
    if (!editingCollection.kg) {
      errors.kg = 'Weight is required';
    } else if (isNaN(editingCollection.kg) || parseFloat(editingCollection.kg) <= 0) {
      errors.kg = 'Valid weight is required';
    }
    
    if (!editingCollection.snf_percentage) {
      errors.snf_percentage = 'SNF percent is required';
    } else if (isNaN(editingCollection.snf_percentage) || parseFloat(editingCollection.snf_percentage) <= 0) {
      errors.snf_percentage = 'Valid SNF percent is required';
    } else if (parseFloat(editingCollection.snf_percentage) > 15) {
      errors.snf_percentage = 'SNF percent cannot exceed 15%';
    }
    
    // CLR is optional, but if provided and not zero, it should be valid
    if (editingCollection.clr && editingCollection.clr.trim() !== '' && parseFloat(editingCollection.clr) !== 0) {
      if (isNaN(editingCollection.clr) || parseFloat(editingCollection.clr) < 0) {
        errors.clr = 'Valid CLR is required';
      } else {
        // Check if CLR has at least 2 digits before decimal
        const clrValue = parseFloat(editingCollection.clr);
        if (clrValue < 10) {
          errors.clr = `CLR value "${editingCollection.clr}" is invalid. Must have at least 2 digits (e.g., 27.50)`;
        }
      }
    }
    
    setEditFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle preview before submission
  const handlePreview = async () => {
    if (!validateEditForm()) {
      return;
    }
    
    const customerId = await ensureCustomerIdForCollection(editingCollection);

    if (!customerId) {
      console.error('Error: No customer ID found in editing collection:', editingCollection);
      setEditFormErrors({
        customer_name: 'Customer selection is required',
        submit: 'Please select a valid customer before continuing'
      });
      return;
    }
    
    const formattedCollection = formatCollectionForAPI(editingCollection, customerId);
    
    console.log('Previewing with customer database ID:', customerId);
    
    setPreviewCollection(formattedCollection);
    setShowPreviewModal(true);
  };

  // Save the edited collection
  const handleSaveEdit = async () => {
    setIsSubmitting(true);
    
    try {
      const customerId = await ensureCustomerIdForCollection(editingCollection);

      if (!customerId) {
        console.error('Error: No customer ID found for saving:', editingCollection);
        setEditFormErrors({
          customer_name: 'Customer selection is required',
          submit: 'Please select a valid customer before saving'
        });
        return;
      }
      
      // Use the formatCollectionForAPI function to get formatted data
      const collectionData = formatCollectionForAPI(editingCollection, customerId);
      
      console.log('Sending update with customer database ID:', customerId);
      console.log('Full data being sent to API:', JSON.stringify(collectionData, null, 2));
      
      // Send update to API
      await updateCollection(editingCollection.id, collectionData);
      
      // Close modals
      setShowPreviewModal(false);
      setShowEditModal(false);
      
      // Refresh collection data
      fetchCollectionsBasedOnFilters();
      
    } catch (err) {
      console.error('Error updating collection:', err);
      setEditFormErrors({ 
        submit: err.message || 'Failed to update collection. Please try again.' 
      });
      setShowPreviewModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to open the customer selection modal for editing
  const openEditCustomerModal = async () => {
    setShowEditCustomerModal(true);
    setEditCustomerSearchTerm('');

    const requestId = Date.now();
    latestCustomerSearchRef.current.edit = requestId;

    try {
      setLoadingCustomers(true);
      const response = await getCustomers({ page_size: 50 });
      if (latestCustomerSearchRef.current.edit !== requestId) {
        return;
      }

      const customersData = response?.results ?? (Array.isArray(response) ? response : []);
      const normalizedCustomers = customersData.map(customer => ({
        ...customer,
        customer_id: customer.customer_id ?? customer.id
      }));

      setCustomers(prev => {
        if (normalizedCustomers.length === 0) {
          return prev;
        }

        const mergedMap = new Map();
        prev.forEach(existingCustomer => {
          const key = existingCustomer.id ?? existingCustomer.customer_id;
          mergedMap.set(key, existingCustomer);
        });
        normalizedCustomers.forEach(customer => {
          const key = customer.id ?? customer.customer_id;
          mergedMap.set(key, customer);
        });
        return Array.from(mergedMap.values());
      });

      setFilteredEditCustomers(normalizedCustomers);
    } catch (err) {
      console.error('Error fetching customers for edit modal:', err);
      setError('Failed to load customers. Please try again later.');
    } finally {
      if (latestCustomerSearchRef.current.edit === requestId) {
        setLoadingCustomers(false);
      }
    }
  };

  // Handle customer search in edit modal
  const handleEditCustomerSearchChange = useCallback(async (e) => {
    const value = e.target.value;
    setEditCustomerSearchTerm(value);

    const trimmedValue = value.trim();
    const requestId = Date.now();
    latestCustomerSearchRef.current.edit = requestId;

    if (!trimmedValue) {
      setFilteredEditCustomers(customers);
      return;
    }

    const params = {
      search: trimmedValue,
      page_size: 50
    };

    if (/^\d+$/.test(trimmedValue)) {
      params.customer_id = trimmedValue;
    }

    try {
      const response = await getCustomers(params);
      if (latestCustomerSearchRef.current.edit !== requestId) {
        return;
      }

      const customersData = response?.results ?? (Array.isArray(response) ? response : []);
      const normalizedCustomers = customersData.map(customer => ({
        ...customer,
        customer_id: customer.customer_id ?? customer.id
      }));

      setCustomers(prev => {
        if (normalizedCustomers.length === 0) {
          return prev;
        }

        const mergedMap = new Map();
        prev.forEach(existingCustomer => {
          const key = existingCustomer.id ?? existingCustomer.customer_id;
          mergedMap.set(key, existingCustomer);
        });
        normalizedCustomers.forEach(customer => {
          const key = customer.id ?? customer.customer_id;
          mergedMap.set(key, customer);
        });
        return Array.from(mergedMap.values());
      });

      setFilteredEditCustomers(normalizedCustomers);
    } catch (err) {
      console.error('Error searching customers for edit modal:', err);
      if (latestCustomerSearchRef.current.edit !== requestId) {
        return;
      }

      const loweredQuery = trimmedValue.toLowerCase();
      const fallbackResults = customers.filter(customer => {
        const idString = customer.customer_id?.toString() || customer.id?.toString() || '';
        const normalizedName = customer.name?.toLowerCase() || '';
        const customerCode = customer.customer_code?.toString() || '';
        return (
          idString.includes(loweredQuery) ||
          normalizedName.includes(loweredQuery) ||
          customerCode.toLowerCase().includes(loweredQuery)
        );
      });

      setFilteredEditCustomers(fallbackResults);
    }
  }, [customers]);

  // Handle customer selection in edit modal
  const selectEditCustomer = (customer) => {
    console.log('Selected customer:', JSON.stringify(customer, null, 2));
    
    // Ensure we're using the database ID (from the 'id' field)
    const customerId = customer.id;
    
    if (!customerId) {
      console.error('Warning: Customer object does not have an ID property:', customer);
    }
    
    const updatedCollection = { 
      ...editingCollection,
      customer: customerId, // Store the actual database ID
      customer_id: customer.customer_id || customerId, // This is for display/reference
      customer_name: customer.name
    };
    
    console.log('Updated collection with customer ID:', customerId);
    
    setEditingCollection(updatedCollection);
    setShowEditCustomerModal(false);
    
    // Clear any customer name validation errors
    if (editFormErrors.customer_name) {
      const updatedErrors = { ...editFormErrors };
      delete updatedErrors.customer_name;
      setEditFormErrors(updatedErrors);
    }
  };

  const calculateTotals = useCallback(() => {
    if (!filteredCollections.length) return null;
    
    return filteredCollections.reduce((totals, collection) => {
      return {
        count: totals.count + 1,
        totalQuantity: totals.totalQuantity + (parseFloat(collection.kg || 0) || 0),
        totalFatKg: totals.totalFatKg + (parseFloat(collection.fat_kg || 0) || 0),
        totalSnfKg: totals.totalSnfKg + (parseFloat(collection.snf_kg || 0) || 0),
        totalSolidWeight: totals.totalSolidWeight + (parseFloat(collection.solid_weight || 0) || 0),
        totalAmount: totals.totalAmount + (parseFloat(collection.amount || 0) || 0)
      };
    }, { count: 0, totalQuantity: 0, totalFatKg: 0, totalSnfKg: 0, totalSolidWeight: 0, totalAmount: 0 });
  }, [filteredCollections]);

  // Add openAddMilkRateModal function
  const openAddMilkRateModal = () => {
    const today = new Date();
    
    // Initialize with current date if not set
    if (!milkRateFromDate) {
      const defaultFromDate = new Date();
      defaultFromDate.setDate(defaultFromDate.getDate() - 7); // Default to 7 days ago
      setMilkRateFromDate(formatDateForInput(defaultFromDate));
    }
    
    if (!milkRateToDate) {
      setMilkRateToDate(formatDateForInput(today));
    }
    
    if (!milkRateSingleDate) {
      setMilkRateSingleDate(formatDateForInput(today));
    }
    
    setShowAddMilkRateModal(true);
  };

  // Add handlePreviewMilkRates function
  const handlePreviewMilkRates = async () => {
    setRatePreviewError(null);
    
    // Validate milk rate
    if (!currentMilkRate.milk_rate) {
      setRatePreviewError('Please enter a valid milk rate');
      return;
    }
    
    // Validate date selection
    if (milkRateSingleDateFilterMode) {
      if (!milkRateSingleDate) {
        setRatePreviewError('Please select a date');
        return;
      }
    } else {
      if (!milkRateFromDate || !milkRateToDate) {
        setRatePreviewError('Please select both from and to dates');
        return;
      }
      
      // Validate date range
      if (new Date(milkRateToDate) < new Date(milkRateFromDate)) {
        setRatePreviewError('To date cannot be earlier than from date');
        return;
      }
    }
    
    try {
      setIsLoadingRatePreview(true);
      
      let params = {};
      
      // Set date parameters based on filter mode
      if (milkRateSingleDateFilterMode) {
        params = {
          dateFrom: milkRateSingleDate,
          dateTo: milkRateSingleDate
        };
      } else {
        params = {
          dateFrom: milkRateFromDate,
          dateTo: milkRateToDate
        };
      }
      
      // Add customer filter if active
      if (selectedCustomer) {
        params.customerId = selectedCustomer.id;
      }
      
      // Fetch collections for preview
      const response = await getFilteredCollections(params);
      
      if (response && response.results) {
        setSelectedCollections(response.results);
        setShowMilkRatePreviewModal(true);
        setShowAddMilkRateModal(false);
      } else {
        setRatePreviewError('No collections found for the selected date range');
      }
    } catch (err) {
      console.error('Error fetching collections for milk rate preview:', err);
      setRatePreviewError('Failed to load collections for preview. Please try again.');
    } finally {
      setIsLoadingRatePreview(false);
    }
  };

  // Add handleMilkRateChange function
  const handleMilkRateChange = (value) => {
    // Update the state even when input is empty
    if (value === '') {
      setCurrentMilkRate({ milk_rate: '' });
      return;
    }

    // Parse the input value
    const numValue = parseFloat(value);
    
    // If it's not a valid number, return without updating
    if (isNaN(numValue)) {
      return;
    }
    
    // Accept any number (including 0)
    setCurrentMilkRate({ milk_rate: numValue });
    
    // Recalculate collections with new milk rate
    const updatedCollections = selectedCollections.map(collection => {
      // Create updated collection with new milk rate
      const updatedCollection = {
        ...collection,
        milk_rate: numValue
      };
      
      // Calculate fat_kg and snf_kg
      const fatKg = Math.floor((updatedCollection.kg * (updatedCollection.fat_percentage / 100)) * 100) / 100;
      const snfKg = Math.floor((updatedCollection.kg * (updatedCollection.snf_percentage / 100)) * 100) / 100;
      
      // Calculate rates based on fat and SNF components
      const fatRate = Math.floor((numValue * 60 / 6.5) * 100) / 100;
      const snfRate = Math.floor((numValue * 40 / updatedCollection.base_snf_percentage) * 100) / 100;
      
      // Calculate amount based on fat, snf rates and components with proper rounding
      const fatAmount = Math.floor(parseFloat(fatKg) * parseFloat(fatRate) * 100) / 100;
      const snfAmount = Math.floor(parseFloat(snfKg) * parseFloat(snfRate) * 100) / 100;
      const amount = parseFloat((fatAmount + snfAmount).toFixed(2));
      
      // Calculate solid weight
      const solidWeight = parseFloat((amount / numValue).toFixed(3));
      
      // Return the updated collection with all recalculated values
      return {
        ...updatedCollection,
        fat_kg: fatKg,
        snf_kg: snfKg,
        fat_rate: fatRate,
        snf_rate: snfRate,
        amount: amount,
        solid_weight: solidWeight
      };
    });
    
    console.log('Updated collections after milk rate change:', updatedCollections[0]);
    setSelectedCollections(updatedCollections);
  };

  // Add handleApplyMilkRates function
  const handleApplyMilkRates = async () => {
    try {
      setIsSubmittingRates(true);
      setRatePreviewError(null);
      
      // Collect success and failure information
      const successfulUpdates = [];
      const failedUpdates = [];
      
      // Track progress for reporting
      const totalCollections = selectedCollections.length;
      
      console.log(`Applying milk rate ${currentMilkRate.milk_rate} to ${totalCollections} collections`);
      
      for (let i = 0; i < selectedCollections.length; i++) {
        const collection = selectedCollections[i];
        
        try {
          // Create a complete API update payload with all calculated values
          const updatedCollection = {
            milk_rate: parseFloat(currentMilkRate.milk_rate),
            fat_kg: collection.fat_kg,
            snf_kg: collection.snf_kg,
            fat_rate: collection.fat_rate,
            snf_rate: collection.snf_rate,
            amount: collection.amount,
            solid_weight: collection.solid_weight
          };
          
          console.log(`Updating collection ID ${collection.id} with milk rate ${currentMilkRate.milk_rate}, API payload:`, updatedCollection);
          
          // Call the API to update the collection
          await updateAddedMilkRateCollection(collection.id, updatedCollection);
          
          // Track successful update
          successfulUpdates.push({
            id: collection.id,
            name: collection.customer_name
          });
        } catch (error) {
          console.error(`Failed to update collection ID ${collection.id}:`, error);
          
          // Track failed update
          failedUpdates.push({
            id: collection.id,
            name: collection.customer_name,
            error: error.error || 'Unknown error'
          });
        }
      }
      
      // Close the modal
      setShowMilkRatePreviewModal(false);
      
      // Reset page to ensure we get fresh data
      setPage(1);
      
      // Refresh the collections with the same date range filter that was used for milk rate preview
      if (milkRateSingleDateFilterMode && milkRateSingleDate) {
        // Temporarily store current filters
        const currentFromDate = fromDate;
        const currentToDate = toDate;
        const currentDateFilterActive = dateFilterActive;
        const currentSingleDateFilterMode = singleDateFilterMode;
        const currentSingleDate = singleDate;
        
        // Set the filters to match the milk rate single date
        setSingleDate(milkRateSingleDate);
        setFromDate('');
        setToDate('');
        setSingleDateFilterMode(true);
        setDateFilterActive(true);
        
        // Fetch with the new filters
        await fetchCollectionsBasedOnFilters();
        
        // Restore original filters if they're different
        if (currentFromDate !== '' || currentToDate !== '' || !currentDateFilterActive || 
            currentSingleDateFilterMode !== true || currentSingleDate !== milkRateSingleDate) {
          setFromDate(currentFromDate);
          setToDate(currentToDate);
          setDateFilterActive(currentDateFilterActive);
          setSingleDateFilterMode(currentSingleDateFilterMode);
          setSingleDate(currentSingleDate);
          
          // Fetch again with restored filters
          await fetchCollectionsBasedOnFilters();
        }
      } else if (milkRateFromDate && milkRateToDate) {
        // Temporarily store current filters
        const currentFromDate = fromDate;
        const currentToDate = toDate;
        const currentDateFilterActive = dateFilterActive;
        const currentSingleDateFilterMode = singleDateFilterMode;
        
        // Set the filters to match the milk rate date range
        setFromDate(milkRateFromDate);
        setToDate(milkRateToDate);
        setDateFilterActive(true);
        setSingleDateFilterMode(false);
        
        // Fetch with the new filters
        await fetchCollectionsBasedOnFilters();
        
        // Restore original filters if they're different
        if (currentFromDate !== milkRateFromDate || currentToDate !== milkRateToDate || 
            !currentDateFilterActive || currentSingleDateFilterMode !== false) {
          setFromDate(currentFromDate);
          setToDate(currentToDate);
          setDateFilterActive(currentDateFilterActive);
          setSingleDateFilterMode(currentSingleDateFilterMode);
          
          // Fetch again with restored filters
          await fetchCollectionsBasedOnFilters();
        }
      } else {
        // If no specific date range was used, just refresh with current filters
        await fetchCollectionsBasedOnFilters();
      }
      
      // Show result message
      if (failedUpdates.length > 0) {
        setError(`Updated ${successfulUpdates.length} collections with milk rate. Failed to update ${failedUpdates.length} collections.`);
      } else {
        setSuccess(`Successfully updated ${successfulUpdates.length} collections with milk rate ${currentMilkRate.milk_rate}.`);
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      console.error('Error applying milk rates:', err);
      setRatePreviewError('Failed to apply milk rates. Please try again.');
    } finally {
      setIsSubmittingRates(false);
    }
  };

  // Add a function to toggle sort order
  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'desc' ? 'asc' : 'desc');
    console.log(`Sort order changed to: ${sortOrder === 'desc' ? 'asc' : 'desc'} (by creation time)`);
  };

  // Handle delete button click
  const handleDeleteClick = (collection) => {
    setCollectionToDelete(collection);
    setShowDeleteConfirmation(true);
    setDeleteError(null);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!collectionToDelete || !collectionToDelete.id) {
      setDeleteError('Invalid collection selected for deletion');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await deleteCollection(collectionToDelete.id);
      
      // Close the delete confirmation modal
      setShowDeleteConfirmation(false);
      setCollectionToDelete(null);
      
      // Refresh the collections to update the UI
      fetchCollectionsBasedOnFilters();
      
      // Show a success message (optional)
      toast.success('Collection deleted successfully');
    } catch (err) {
      console.error('Error deleting collection:', err);
      setDeleteError(err.message || 'Failed to delete the collection. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="collections-container">
      <header className="collections-header">
        <div className="header-left">
          <Link to="/dashboard" className="back-button">
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>Back</span>
          </Link>
        </div>
        <h1>Collections Preview</h1>
        <div className="header-right">
          <button className="logout-button" onClick={handleLogout}>
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <main className="collections-content">
        {error ? (
          <div className="error-message">
            <p>{error}</p>
            <button onClick={fetchCollectionsBasedOnFilters}>Retry</button>
          </div>
        ) : success ? (
          <div className="success-message">
            <p>{success}</p>
          </div>
        ) : (
          <>
            <div className="collections-filters">
              <div className="search-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search by customer name or ID..." 
                  className="search-input"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <FontAwesomeIcon 
                    icon={faTimes} 
                    className="clear-search-icon" 
                    onClick={clearSearch}
                  />
                )}
              </div>
              
              <div className="filter-options">
                <button 
                  className="filter-button customer-filter-button" 
                  onClick={openCustomerModal}
                >
                  <FontAwesomeIcon icon={faUserAlt} />
                  <span>Filter by Customer</span>
                </button>
                
                <button 
                  className="filter-button date-filter-button" 
                  onClick={openDateFilterModal}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <span>Filter by Date</span>
                </button>
                
                {/* Add a Sort Order button */}
                <button 
                  className="filter-button sort-order-button" 
                  onClick={toggleSortOrder}
                  title={sortOrder === 'desc' ? 'Newest first by creation time (Change to oldest first)' : 'Oldest first by creation time (Change to newest first)'}
                >
                  <FontAwesomeIcon icon={sortOrder === 'desc' ? faSortAmountDown : faSortAmountUp} />
                  <span>{sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}</span>
                </button>
                
                <button 
                  className="filter-button milk-rate-button" 
                  onClick={openAddMilkRateModal}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  <span>Change Milk Rate</span>
                </button>
                
                {/* Add Pro-Rata Report Generator Component */}
                <div className="pro-rata-report-generator-container" style={{marginTop: '-15px'}}>
                  <ProRataReportGenerator />
                </div>
              </div>
              
              {(selectedCustomer || dateFilterActive) && (
                <div className="active-filters-summary">
                  <p>
                    <strong>Active Filters:</strong>{' '}
                    {selectedCustomer && dateFilterActive
                      ? singleDateFilterMode
                        ? `Showing collections for ${selectedCustomer.name} on ${formatDateForDisplay(singleDate)}`
                        : `Showing collections for ${selectedCustomer.name} from ${formatDateForDisplay(fromDate)} to ${formatDateForDisplay(toDate)}`
                      : selectedCustomer
                      ? `Showing collections for ${selectedCustomer.name}`
                      : dateFilterActive
                      ? singleDateFilterMode
                        ? `Showing all collections on ${formatDateForDisplay(singleDate)}`
                        : `Showing all collections from ${formatDateForDisplay(fromDate)} to ${formatDateForDisplay(toDate)}`
                      : ''}
                  </p>
                  
                  <p>
                    <span title="Current sort order">
                      <strong>Sorting:</strong> {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'} by creation time
                    </span>
                  </p>
                  
                  <p className="filter-action-buttons">
                    {selectedCustomer && (
                      <button 
                        className="clear-filter-button" 
                        onClick={clearCustomerFilter}
                        title="Clear customer filter"
                      >
                        <FontAwesomeIcon icon={faTimes} /> Clear customer
                      </button>
                    )}
                    
                    {dateFilterActive && (
                      <button 
                        className="clear-filter-button" 
                        onClick={clearDateFilter}
                        title="Clear date filter"
                      >
                        <FontAwesomeIcon icon={faTimes} /> Clear dates
                      </button>
                    )}
                  </p>
                </div>
              )}
              
              {/* Show sort order info even when no other filters are active */}
              {!selectedCustomer && !dateFilterActive && filteredCollections.length > 0 && (
                <div className="active-filters-summary">
                  <p>
                    <span title="Current sort order">
                      <strong>Sorting:</strong> {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'} by creation time
                    </span>
                  </p>
                </div>
              )}
            </div>

            {filteredCollections.length === 0 && !loading ? (
              <div className="no-collections">
                {searchTerm || selectedCustomer || dateFilterActive ? (
                  <p>
                    No collections found
                    {searchTerm && ` matching "${searchTerm}"`}
                    {selectedCustomer && ` for customer "${selectedCustomer.name}"`}
                    {dateFilterActive && singleDateFilterMode
                      ? ` on ${formatDateForDisplay(singleDate)}`
                      : dateFilterActive
                      ? ` between ${formatDateForDisplay(fromDate)} and ${formatDateForDisplay(toDate)}`
                      : ''}.
                  </p>
                ) : (
                  <p>No collections found.</p>
                )}
              </div>
            ) : (
              <div className="collections-table-container">
                <table className="collections-table">
                  <thead>
                    <tr>
                      <th>S.No.</th>
                      <th>Date</th>
                      <th>Milk Rate</th>
                      <th>Name</th>
                      <th>Qty (kg)</th>
                      <th>Fat %</th>
                      <th>SNF %</th>
                      <th>CLR</th>
                      <th>Fat (kg)</th>
                      <th>SNF (kg)</th>
                      <th>Fat Rate</th>
                      <th>SNF Rate</th>
                      <th>Base SNF</th>
                      <th>Amount</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCollections.map((collection, index) => (
                      <tr className="collection-row" key={collection.id}>
                        <td>{index + 1}</td>
                        <td>{formatDate(collection.collection_date, collection.collection_time)}</td>
                        <td>{formatNumber(collection.milk_rate)}</td>
                        <td className="customer-name-cell">
                          <span style={{fontSize: '15px'}}>{collection.customer_id || 'N/A'}</span> - {collection.customer_name || 'N/A'}
                        </td>
                        <td>{formatNumber(collection.kg)}</td>
                        <td>{formatNumber(collection.fat_percentage)}</td>
                        <td 
                          style={{ 
                            opacity: parseFloat(collection.snf_percentage) > 0 ? '1' : '0.5',
                            backgroundColor: parseFloat(collection.snf_percentage) > 0 ? '#e6f7ff' : 'inherit'
                          }}
                        >
                          {formatNumber(collection.snf_percentage)}
                        </td>
                        <td 
                          style={{ 
                            opacity: parseFloat(collection.clr) > 0 ? '1' : '0.5',
                            backgroundColor: parseFloat(collection.clr) > 0 ? '#e6f7ff' : 'inherit'
                          }}
                        >
                          {formatNumber(collection.clr)}
                        </td>
                        <td>{formatNumber(collection.fat_kg)}</td>
                        <td>{formatNumber(collection.snf_kg)}</td>
                        <td>{formatNumber(collection.fat_rate)}</td>
                        <td>{formatNumber(collection.snf_rate)}</td>
                        <td>{formatNumber(collection.base_snf_percentage || collection.base_snf || 9.0)}</td>
                        <td>{formatNumber(collection.amount)}</td>
                        <td className="actions-cell">
                          <button 
                            className="edit-button" 
                            onClick={() => handleEditClick(collection)}
                            title="Edit collection"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button 
                            className="delete-button" 
                            onClick={() => handleDeleteClick(collection)}
                            title="Delete collection"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(searchTerm || selectedCustomer || dateFilterActive) && filteredCollections.length > 0 && (
              <div className="collection-summary-stats">
                <div className="summary-header">
                  <h3>Collection Summary</h3>
                </div>
                
                {(() => {
                  const totals = calculateTotals();
                  if (!totals) return null;
                  
                  return (
                    <>
                      <div className="stats-container">
                        <div className="stat-item">
                          <div className="stat-label">Total Quantity</div>
                          <div className="stat-value">{formatNumber(totals.totalQuantity)} kg</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Total Fat</div>
                          <div className="stat-value">{formatNumber(totals.totalFatKg)} kg</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Total SNF</div>
                          <div className="stat-value">{formatNumber(totals.totalSnfKg)} kg</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Total Solid Weight</div>
                          <div className="stat-value">{formatNumber(totals.totalSolidWeight)} kg</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-label">Total Amount</div>
                          <div className="stat-value">{formatCurrency(totals.totalAmount)}</div>
                        </div>
                      </div>
                      
                      <div className="stat-footer">
                        <span>Final Amount: </span>
                        <span className="final-amount">{formatCurrency(Math.floor(totals.totalAmount * 0.999))}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}

            {loading && (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading collections...</p>
              </div>
            )}

            {showLoadMoreButton && (
              <div className="load-more">
                <button onClick={() => loadMoreCollections()} className="load-more-button">
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </main>
      
      {showCustomerModal && (
        <div className="modal-overlay">
          <div className="modal-content customer-modal">
            <div className="modal-header">
              <h2>Select Customer</h2>
              <button 
                className="modal-close-button" 
                onClick={() => setShowCustomerModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="customer-search-container">
              <div className="search-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search customers..." 
                  className="search-input"
                  value={customerSearchTerm}
                  onChange={handleCustomerSearchChange}
                  autoFocus
                />
              </div>
            </div>
            
            <div className="customers-list">
              {loadingCustomers ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading customers...</p>
                </div>
              ) : filteredCustomers.length === 0 ? (
                <div className="no-customers">
                  <p>No customers found.</p>
                </div>
              ) : (
                filteredCustomers.map(customer => (
                  <div 
                    key={customer.id} 
                    className="customer-item"
                    onClick={() => selectCustomer(customer)}
                  >
                    <div className="customer-name">{customer.name}</div>
                    <div className="customer-code">{customer.customer_id || customer.id || 'No ID'}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      
      {showDateFilterModal && (
        <div className="modal-overlay">
          <div className="modal-content date-filter-modal">
            <div className="modal-header">
              <h2>Filter by Date</h2>
              <button 
                className="modal-close-button" 
                onClick={() => setShowDateFilterModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="date-filter-container">
              <div style={styles.filterTypeToggle}>
                <label 
                  style={{
                    ...styles.toggleOption,
                    ...(singleDateFilterMode ? styles.inactiveToggle : styles.activeToggle)
                  }}
                >
                  <input
                    type="radio"
                    name="date-filter-type"
                    checked={!singleDateFilterMode}
                    onChange={() => setSingleDateFilterMode(false)}
                    style={styles.toggleInput}
                  />
                  <span>Date Range</span>
                </label>
                <label 
                  style={{
                    ...styles.toggleOption,
                    ...(singleDateFilterMode ? styles.activeToggle : styles.inactiveToggle)
                  }}
                >
                  <input
                    type="radio"
                    name="date-filter-type"
                    checked={singleDateFilterMode}
                    onChange={() => setSingleDateFilterMode(true)}
                    style={styles.toggleInput}
                  />
                  <span>Single Date</span>
                </label>
              </div>
              
              {singleDateFilterMode ? (
                <div className="date-input-group">
                  <label htmlFor="single-date">Select Date:</label>
                  <input
                    id="single-date"
                    type="date"
                    className="date-input"
                    value={singleDate}
                    onChange={(e) => setSingleDate(e.target.value)}
                    onClick={(e) => e.target.showPicker()}
                  />
                </div>
              ) : (
                <>
                  <div className="date-input-group">
                    <label htmlFor="from-date">From Date:</label>
                    <input
                      id="from-date"
                      type="date"
                      className="date-input"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      onClick={(e) => e.target.showPicker()}
                    />
                  </div>
                  
                  <div className="date-input-group">
                    <label htmlFor="to-date">To Date:</label>
                    <input
                      id="to-date"
                      type="date"
                      className="date-input"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      onClick={(e) => e.target.showPicker()}
                    />
                  </div>
                </>
              )}
              
              <div className="date-filter-actions">
                <button 
                  className="date-filter-button secondary" 
                  onClick={() => setShowDateFilterModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="date-filter-button primary" 
                  onClick={applyDateFilter}
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showEditModal && editingCollection && (
        <div className="modal-overlay">
          <div className="modal-content edit-collection-modal">
            <div className="modal-header">
              <h2>Edit Collection</h2>
              <button 
                className="modal-close-button" 
                onClick={() => setShowEditModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="edit-collection-container">
              {editFormErrors.submit && (
                <div className="edit-form-error">
                  <p>{editFormErrors.submit}</p>
                </div>
              )}
              
              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="customer-name">Customer:</label>
                  <div className="customer-select-container">
                    <input 
                      id="customer-name"
                      type="text" 
                      value={editingCollection.customer_name || ''}
                      readOnly
                      className={`edit-form-input ${editFormErrors.customer_name ? 'error' : ''}`}
                    />
                    <button 
                      type="button" 
                      className="customer-select-button"
                      onClick={openEditCustomerModal}
                    >
                      <FontAwesomeIcon icon={faUserAlt} />
                      <span>Select</span>
                    </button>
                  </div>
                  {editFormErrors.customer_name && <div className="edit-input-error">{editFormErrors.customer_name}</div>}
                </div>
                
                <div className="edit-form-group">
                  <label htmlFor="collection-date">Collection Date:</label>
                  <input 
                    id="collection-date"
                    type="date" 
                    value={editingCollection.collection_date || ''}
                    onChange={(e) => handleEditInputChange('collection_date', e.target.value)}
                    className={`edit-form-input ${editFormErrors.collection_date ? 'error' : ''}`}
                    onClick={(e) => e.target.showPicker()}
                  />
                  {editFormErrors.collection_date && <div className="edit-input-error">{editFormErrors.collection_date}</div>}
                </div>
              </div>
              
              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="edit-time">Collection Time:</label>
                  <select
                    id="edit-time"
                    value={editingCollection.collection_time === 'morning' ? 'morning' : 'evening'}
                    onChange={(e) => handleEditInputChange('collection_time', e.target.value)}
                    className="edit-form-input"
                  >
                    <option value="morning">Morning (AM)</option>
                    <option value="evening">Evening (PM)</option>
                  </select>
                </div>
                
                <div className="edit-form-group">
                  <label htmlFor="edit-kg">Weight (kg):</label>
                  <input 
                    id="edit-kg"
                    type="number" 
                    step="0.01"
                    min="0.01"
                    value={editingCollection.kg}
                    onChange={(e) => handleEditInputChange('kg', e.target.value)}
                    className={`edit-form-input ${editFormErrors.kg ? 'error' : ''}`}
                  />
                  {editFormErrors.kg && <div className="edit-input-error">{editFormErrors.kg}</div>}
                </div>
              </div>
              
              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="edit-fat">Fat Percentage:</label>
                  <input 
                    id="edit-fat"
                    type="number" 
                    step="0.01"
                    min="0.01"
                    max="15"
                    value={editingCollection.fat_percentage}
                    onChange={(e) => handleEditInputChange('fat_percentage', e.target.value)}
                    className={`edit-form-input ${editFormErrors.fat_percentage ? 'error' : ''}`}
                  />
                  {editFormErrors.fat_percentage && <div className="edit-input-error">{editFormErrors.fat_percentage}</div>}
                </div>
                
                <div className="edit-form-group">
                  <label htmlFor="edit-clr">CLR:</label>
                  <input 
                    id="edit-clr"
                    type="text" 
                    value={editingCollection.clr}
                    onChange={(e) => handleEditInputChange('clr', e.target.value)}
                    className={`edit-form-input ${editFormErrors.clr ? 'error' : ''}`}
                  />
                  {editFormErrors.clr && <div className="edit-input-error">{editFormErrors.clr}</div>}
                </div>
              </div>
              
              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="edit-snf">SNF Percentage:</label>
                  <input 
                    id="edit-snf"
                    type="number" 
                    step="0.01"
                    min="0.01"
                    max="15"
                    value={editingCollection.snf_percentage}
                    onChange={(e) => handleEditInputChange('snf_percentage', e.target.value)}
                    className={`edit-form-input ${editFormErrors.snf_percentage ? 'error' : ''}`}
                  />
                  {editFormErrors.snf_percentage && <div className="edit-input-error">{editFormErrors.snf_percentage}</div>}
                </div>
                
                <div className="edit-form-group">
                  <label htmlFor="edit-base-snf">Base SNF %:</label>
                  <input 
                    id="edit-base-snf"
                    type="number" 
                    step="0.1"
                    min="8.0"
                    max="10.0"
                    value={editingCollection.base_snf_percentage || editingCollection.base_snf}
                    onChange={(e) => handleEditInputChange('base_snf_percentage', e.target.value)}
                    className="edit-form-input"
                  />
                </div>
              </div>
              
              <div className="edit-form-row">
                <div className="edit-form-group">
                  <label htmlFor="edit-rate">Milk Rate:</label>
                  <input 
                    id="edit-rate"
                    type="number" 
                    step="0.01"
                    min="0.01"
                    value={editingCollection.milk_rate}
                    onChange={(e) => handleEditInputChange('milk_rate', e.target.value)}
                    className="edit-form-input"
                  />
                </div>
              </div>
              
              <div className="edit-form-row calculation-results">
                <div className="calculation-item">
                  <label>Fat (kg):</label>
                  <span>{formatNumber(editingCollection.fat_kg)}</span>
                </div>
                
                <div className="calculation-item">
                  <label>SNF (kg):</label>
                  <span>{formatNumber(editingCollection.snf_kg)}</span>
                </div>
                
                <div className="calculation-item">
                  <label>Amount:</label>
                  <span>{formatCurrency(editingCollection.amount)}</span>
                </div>
              </div>
              
              <div className="edit-form-actions">
                <button 
                  className="edit-form-button cancel" 
                  onClick={() => setShowEditModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  className="edit-form-button preview" 
                  onClick={handlePreview}
                  disabled={isSubmitting}
                >
                  <FontAwesomeIcon icon={faEye} />
                  Preview Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showPreviewModal && previewCollection && (
        <div className="modal-overlay">
          <div className="preview-modal" style={{ width: '95%', maxWidth: '1400px', maxHeight: '95vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Preview Edited Collection</h2>
              <button 
                className="close-modal-button"
                onClick={() => setShowPreviewModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body" style={{ maxHeight: '70vh' }}>
              <div className="preview-header">
                <p className="preview-info">Please review the edited collection before saving:</p>
              </div>
              
              <div className="preview-table-container" style={{ maxWidth: '100%', overflowX: 'auto', marginTop: '15px' }}>
                <table className="preview-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 2px', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Customer</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Weight (kg)</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Milk Type</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Collection Time</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Fat %</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>CLR</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>SNF %</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Fat KG</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>SNF KG</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Fat Rate</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>SNF Rate</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Base SNF %</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Milk Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{previewCollection.customer_name}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatDate(previewCollection.collection_date, previewCollection.collection_time)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatNumber(previewCollection.kg)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{previewCollection.milk_type}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{previewCollection.collection_time === 'morning' ? 'Morning' : 'Evening'}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{previewCollection.fat_percentage}%</td>
                      <td style={{ 
                        padding: '10px 12px', 
                        textAlign: 'left', 
                        borderBottom: '1px solid #e0e0e0', 
                        whiteSpace: 'nowrap',
                        opacity: parseFloat(previewCollection.clr) > 0 ? '1' : '0.5',
                        backgroundColor: parseFloat(previewCollection.clr) > 0 ? '#e6f7ff' : 'inherit'
                      }}>{formatNumber(previewCollection.clr)}</td>
                      <td style={{ 
                        padding: '10px 12px', 
                        textAlign: 'left', 
                        borderBottom: '1px solid #e0e0e0', 
                        whiteSpace: 'nowrap',
                        opacity: parseFloat(previewCollection.snf_percentage) > 0 ? '1' : '0.5',
                        backgroundColor: parseFloat(previewCollection.snf_percentage) > 0 ? '#e6f7ff' : 'inherit'
                      }}>{formatNumber(previewCollection.snf_percentage)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatNumber(previewCollection.fat_kg)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatNumber(previewCollection.snf_kg)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatNumber(previewCollection.fat_rate)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatNumber(previewCollection.snf_rate)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatNumber(previewCollection.base_snf_percentage || previewCollection.base_snf || 9.0)}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{formatNumber(previewCollection.milk_rate)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowPreviewModal(false)}
              >
                Back to Edit
              </button>
              <button 
                className="confirm-button"
                onClick={handleSaveEdit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Confirm & Save</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showEditCustomerModal && (
        <div className="modal-overlay">
          <div className="modal-content customer-modal">
            <div className="modal-header">
              <h2>Select Customer</h2>
              <button 
                className="modal-close-button" 
                onClick={() => setShowEditCustomerModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="customer-search-container">
              <div className="search-wrapper">
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                <input 
                  type="text" 
                  placeholder="Search customers..." 
                  className="search-input"
                  value={editCustomerSearchTerm}
                  onChange={handleEditCustomerSearchChange}
                  autoFocus
                />
              </div>
            </div>
            
            <div className="customers-list">
              {loadingCustomers ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Loading customers...</p>
                </div>
              ) : filteredEditCustomers.length === 0 ? (
                <div className="no-customers">
                  <p>No customers found.</p>
                </div>
              ) : (
                filteredEditCustomers.map(customer => (
                  <div 
                    key={customer.id} 
                    className="customer-item"
                    onClick={() => selectEditCustomer(customer)}
                  >
                    <div className="customer-name">{customer.name}</div>
                    <div className="customer-code">{customer.customer_id || customer.id || 'No ID'}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      
      {showAddMilkRateModal && (
        <div className="modal-overlay">
          <div className="modal-content date-filter-modal">
            <div className="modal-header">
              <h2>Filter Collections for Milk Rate Update</h2>
              <button 
                className="modal-close-button" 
                onClick={() => setShowAddMilkRateModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="date-filter-container">
              <div style={styles.filterTypeToggle}>
                <label 
                  style={{
                    ...styles.toggleOption,
                    ...(milkRateSingleDateFilterMode ? styles.inactiveToggle : styles.activeToggle)
                  }}
                >
                  <input
                    type="radio"
                    name="milk-rate-date-filter-type"
                    checked={!milkRateSingleDateFilterMode}
                    onChange={() => setMilkRateSingleDateFilterMode(false)}
                    style={styles.toggleInput}
                  />
                  <span>Date Range</span>
                </label>
                <label 
                  style={{
                    ...styles.toggleOption,
                    ...(milkRateSingleDateFilterMode ? styles.activeToggle : styles.inactiveToggle)
                  }}
                >
                  <input
                    type="radio"
                    name="milk-rate-date-filter-type"
                    checked={milkRateSingleDateFilterMode}
                    onChange={() => setMilkRateSingleDateFilterMode(true)}
                    style={styles.toggleInput}
                  />
                  <span>Single Date</span>
                </label>
              </div>
              
              {milkRateSingleDateFilterMode ? (
                <div className="date-input-group">
                  <label htmlFor="milk-rate-single-date">Select Date:</label>
                  <input
                    id="milk-rate-single-date"
                    type="date"
                    className="date-input"
                    value={milkRateSingleDate}
                    onChange={(e) => setMilkRateSingleDate(e.target.value)}
                    onClick={(e) => e.target.showPicker()}
                  />
                </div>
              ) : (
                <>
                  <div className="date-input-group">
                    <label htmlFor="from-date">From Date:</label>
                    <input
                      id="from-date"
                      type="date"
                      className="date-input"
                      value={milkRateFromDate}
                      onChange={(e) => setMilkRateFromDate(e.target.value)}
                      onClick={(e) => e.target.showPicker()}
                    />
                  </div>
                  
                  <div className="date-input-group">
                    <label htmlFor="to-date">To Date:</label>
                    <input
                      id="to-date"
                      type="date"
                      className="date-input"
                      value={milkRateToDate}
                      onChange={(e) => setMilkRateToDate(e.target.value)}
                      onClick={(e) => e.target.showPicker()}
                    />
                  </div>
                </>
              )}
              
              {ratePreviewError && (
                <div className="error-message" style={{ color: 'red', margin: '10px 0' }}>
                  {ratePreviewError}
                </div>
              )}
              
              <div className="date-filter-actions">
                <button 
                  className="date-filter-button secondary" 
                  onClick={() => setShowAddMilkRateModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="date-filter-button primary" 
                  onClick={handlePreviewMilkRates}
                >
                  {isLoadingRatePreview ? (
                    <span>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      <span style={{ marginLeft: '8px' }}>Loading...</span>
                    </span>
                  ) : 'Apply Filter'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showMilkRatePreviewModal && (
        <div className="modal-overlay" onClick={() => setShowMilkRatePreviewModal(false)}>
          <div className="modal-content preview-modal" onClick={e => e.stopPropagation()} style={{ width: '95%', maxWidth: '1400px', maxHeight: '95vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Preview Milk Rate Calculations</h2>
              <button 
                className="close-modal-button"
                onClick={() => setShowMilkRatePreviewModal(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="modal-body" style={{ maxHeight: '70vh' }}>
              {ratePreviewError && (
                <div className="edit-form-error" style={{ marginBottom: '15px', color: 'red', fontWeight: 'bold' }}>{ratePreviewError}</div>
              )}
              
              <div className="preview-header">
                <h3 style={{ marginBottom: '2px' }}>
                  {milkRateSingleDateFilterMode 
                    ? `Collections on ${formatDateForDisplay(milkRateSingleDate)}`
                    : `Collections from ${formatDateForDisplay(milkRateFromDate)} to ${formatDateForDisplay(milkRateToDate)}`}
                  <span className="collections-count" style={{ marginLeft: '10px', fontSize: '0.9em', color: '#666' }}>
                    {selectedCollections.length} items
                  </span>
                </h3>
                
                <div className="rate-settings" style={{ marginTop: '15px', marginBottom: '15px' }}>
                  <div className="rate-input-group" style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ fontWeight: 'bold', marginRight: '10px' }}>Milk Rate</label>
                    <input
                      type="number"
                      className="rate-input"
                      placeholder="e.g: 50"
                      value={currentMilkRate.milk_rate}
                      onChange={e => handleMilkRateChange(e.target.value)}
                      step="0.1"
                      style={{ 
                        padding: '8px 12px', 
                        border: '1px solid #ccc', 
                        borderRadius: '4px',
                        width: '100px'
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="preview-table-container" style={{ maxWidth: '100%', overflowX: 'auto' }}>
                <table className="preview-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Date & Time</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Customer</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Weight (kg)</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Fat %</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>SNF %</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>CLR</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Fat Kg</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>SNF Kg</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Fat Rate</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>SNF Rate</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Base SNF</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Milk Rate</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f5f5f5', fontWeight: '600', color: '#444' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCollections.map((collection, index) => {
                      // Double-check calculation of values
                      const fatKg = parseFloat(collection.fat_kg) || 
                        (parseFloat(collection.kg) * parseFloat(collection.fat_percentage) / 100);
                     
                      const snfKg = parseFloat(collection.snf_kg) || 
                        (parseFloat(collection.kg) * parseFloat(collection.snf_percentage) / 100);
                      
                      return (
                        <tr key={collection.id || index}>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatDate(collection.collection_date, collection.collection_time)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {collection.customer_name || 'Unknown'}
                            <div style={{ fontSize: '13px', color: '#666' }}>ID: {collection.customer_id}</div>
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(collection.kg)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(collection.fat_percentage)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(collection.snf_percentage)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(collection.clr)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(fatKg)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(snfKg)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(collection.fat_rate)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(collection.snf_rate)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(collection.base_snf_percentage)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatNumber(collection.milk_rate)}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>
                            {formatCurrency(collection.amount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="2" style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                        <strong>Totals</strong>
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                        {formatNumber(selectedCollections.reduce((sum, c) => sum + parseFloat(c.kg || 0), 0))}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9' }}>-</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9' }}>-</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9' }}>-</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                        {formatNumber(selectedCollections.reduce((sum, c) => {
                          const fatKg = parseFloat(c.fat_kg) || 
                            (parseFloat(c.kg) * parseFloat(c.fat_percentage) / 100);
                          return sum + fatKg;
                        }, 0))}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                        {formatNumber(selectedCollections.reduce((sum, c) => {
                          const snfKg = parseFloat(c.snf_kg) || 
                            (parseFloat(c.kg) * parseFloat(c.snf_percentage) / 100);
                          return sum + snfKg;
                        }, 0))}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9' }}>-</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9' }}>-</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9' }}>-</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9' }}>-</td>
                      <td style={{ padding: '10px 12px', textAlign: 'left', backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                        {formatCurrency(selectedCollections.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              <div className="preview-summary" style={{ 
                marginBottom: '0px', 
                padding: '20px', 
                borderRadius: '6px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '8px 15px', 
                  borderBottom: '1px solid #e9ecef',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{ margin: '0', fontSize: '14px', color: '#495057' }}>Collection Summary</h3>
                  <span style={{marginLeft: '10px', fontSize: '14px', color: '#181818' }}>{selectedCollections.length} items</span>
                </div>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: '#fff'
                }}>
                  <div className="summary-card" style={{ 
                    padding: '8px 10px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px',
                    borderLeft: '3px solid #28a745'
                  }}>
                    <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>Weight</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#212529' }}>
                      {formatNumber(selectedCollections.reduce((sum, c) => sum + parseFloat(c.kg || 0), 0))} kg
                    </div>
                  </div>
                  
                  <div className="summary-card" style={{ 
                    padding: '8px 10px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px',
                    borderLeft: '3px solid #fd7e14'
                  }}>
                    <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>Fat</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#212529' }}>
                      {formatNumber(selectedCollections.reduce((sum, c) => {
                        const fatKg = parseFloat(c.fat_kg) || 
                          (parseFloat(c.kg) * parseFloat(c.fat_percentage) / 100);
                        return sum + fatKg;
                      }, 0))} kg
                    </div>
                  </div>
                  
                  <div className="summary-card" style={{ 
                    padding: '8px 10px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px',
                    borderLeft: '3px solid #17a2b8'
                  }}>
                    <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>SNF</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#212529' }}>
                      {formatNumber(selectedCollections.reduce((sum, c) => {
                        const snfKg = parseFloat(c.snf_kg) || 
                          (parseFloat(c.kg) * parseFloat(c.snf_percentage) / 100);
                        return sum + snfKg;
                      }, 0))} kg
                    </div>
                  </div>
                  
                  <div className="summary-card" style={{ 
                    padding: '8px 10px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '4px',
                    borderLeft: '3px solid #6f42c1'
                  }}>
                    <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '2px' }}>Solid</div>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#212529' }}>
                      {formatNumber(selectedCollections.reduce((sum, c) => 
                        sum + parseFloat(c.solid_weight || 0), 0))} kg
                    </div>
                  </div>
                </div>
                
                <div style={{ 
                  padding: '10px 15px', 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e9ecef',
                }}>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6c757d' }}>Total Amount</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#212529' }}>
                        {formatCurrency(selectedCollections.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0))}
                      </div>
                    </div>
                    
                    <div>
                      <div style={{ fontSize: '11px', color: '#155724' }}>Final Amount</div>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#155724' }}>
                        {formatCurrency(Math.floor(selectedCollections.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0) * 0.999))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="cancel-button"
                onClick={() => setShowMilkRatePreviewModal(false)}
              >
                Cancel
              </button>
              <button 
                className="confirm-button"
                onClick={handleApplyMilkRates}
                disabled={isSubmittingRates}
              >
                {isSubmittingRates ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    <span>Applying...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} />
                    <span>Apply Rate Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showDeleteConfirmation && collectionToDelete && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button 
                className="modal-close-button" 
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setCollectionToDelete(null);
                  setDeleteError(null);
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this collection?</p>
              <div className="collection-delete-info">
                <p><strong>Customer:</strong> {collectionToDelete.customer_name}</p>
                <p><strong>Date:</strong> {formatDateForDisplay(collectionToDelete.collection_date)}</p>
                <p><strong>Time:</strong> {collectionToDelete.collection_time === 'morning' ? 'Morning' : 'Evening'}</p>
                <p><strong>Weight:</strong> {parseFloat(collectionToDelete.kg).toFixed(2)} kg</p>
              </div>
              <p className="warning-text">This action cannot be undone.</p>
              {deleteError && (
                <div className="error-message">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="cancel-button" 
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setCollectionToDelete(null);
                  setDeleteError(null);
                }}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="delete-button" 
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <div className="button-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <footer className="collections-footer">
        <p>&copy; 2025 Dudhiya. All rights reserved.</p>
        <img src="/Powered-By-Netpy-Technologies.png" style={{width: '170px'}} alt="Powered by Netpy Technologies" />
      </footer>
    </div>
  );
};

export default PreviewCollections; 